const HANDOFF_VERSION = '1';
const HANDOFF_PROPERTY_NAMES = [
  'ph_handoff',
  'ph_environment',
  'ph_distinct_id',
  'ph_session_id',
];
const APP_ORIGINS_BY_ENVIRONMENT = {
  development: 'https://dev.questri.app',
  production: 'https://questri.app',
};
export const DEFAULT_POSTHOG_LOCAL_APP_ORIGIN = 'http://localhost:3000';
const LOCAL_APP_ORIGIN_PATTERN = /^http:\/\/(?:localhost|127\.0\.0\.1):([1-9][0-9]{0,4})$/;
const ANONYMOUS_DISTINCT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const ANONYMOUS_SESSION_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_SESSION_CLOCK_SKEW_MS = 5 * 60 * 1000;

export const getPostHogLocalAppOrigin = (configuredOrigin) => {
  let normalizedOrigin = '';
  if (typeof configuredOrigin === 'string') {
    normalizedOrigin = configuredOrigin.trim();
  }
  const localAppOrigin = normalizedOrigin || DEFAULT_POSTHOG_LOCAL_APP_ORIGIN;
  const match = localAppOrigin.match(LOCAL_APP_ORIGIN_PATTERN);
  if (!match || Number(match[1]) > 65535) {
    return null;
  }

  try {
    const localAppUrl = new URL(localAppOrigin);
    if (localAppUrl.origin !== localAppOrigin) {
      return null;
    }
    return localAppOrigin;
  } catch (error) {
    return null;
  }
};

export const isValidAnonymousDistinctId = (value) => (
  typeof value === 'string'
  && value === value.trim()
  && ANONYMOUS_DISTINCT_ID_PATTERN.test(value)
);

export const isValidAnonymousSessionId = (value, now = Date.now()) => {
  if (
    typeof value !== 'string'
    || value !== value.trim()
    || !ANONYMOUS_SESSION_ID_PATTERN.test(value)
  ) {
    return false;
  }

  const sessionTimestamp = Number.parseInt(value.replace(/-/g, '').slice(0, 12), 16);
  return Number.isSafeInteger(sessionTimestamp)
    && sessionTimestamp <= now + MAX_SESSION_CLOCK_SKEW_MS
    && sessionTimestamp >= now - MAX_SESSION_AGE_MS;
};

const hasPostHogProperties = (parameters) => (
  Array.from(parameters.keys()).some((propertyName) => (
    propertyName.toLowerCase().startsWith('ph_')
  ))
);

const hasCompleteHandoffTuple = (parameters) => {
  const postHogPropertyNames = Array.from(parameters.keys())
    .filter((propertyName) => propertyName.toLowerCase().startsWith('ph_'));
  return postHogPropertyNames.length === HANDOFF_PROPERTY_NAMES.length
    && HANDOFF_PROPERTY_NAMES.every((propertyName) => (
      parameters.getAll(propertyName).length === 1
    ))
    && postHogPropertyNames.every((propertyName) => (
      HANDOFF_PROPERTY_NAMES.includes(propertyName)
    ));
};

const isUnambiguousHash = (rawHash) => (
  !rawHash
  || rawHash.split('&').every((segment) => segment && segment.includes('='))
);

const stripPostHogFragment = ({
  historyObject,
  locationUrl,
}) => {
  const rawHash = locationUrl.hash.slice(1);
  const hashParameters = new URLSearchParams(rawHash);
  const containsPostHogProperties = hasPostHogProperties(hashParameters);
  if (!containsPostHogProperties) {
    return {
      hashParameters,
      rawHash,
      stripped: true,
    };
  }

  HANDOFF_PROPERTY_NAMES.forEach((propertyName) => {
    hashParameters.delete(propertyName);
  });
  Array.from(hashParameters.keys()).forEach((propertyName) => {
    if (propertyName.toLowerCase().startsWith('ph_')) {
      hashParameters.delete(propertyName);
    }
  });

  const cleanedLocationUrl = new URL(locationUrl.href);
  cleanedLocationUrl.hash = hashParameters.toString();
  try {
    historyObject.replaceState(
      historyObject.state,
      '',
      `${cleanedLocationUrl.pathname}${cleanedLocationUrl.search}${cleanedLocationUrl.hash}`,
    );
  } catch (error) {
    return {
      hashParameters: new URLSearchParams(rawHash),
      rawHash,
      stripped: false,
    };
  }

  return {
    hashParameters: new URLSearchParams(rawHash),
    rawHash,
    stripped: true,
  };
};

export const consumePostHogHandoff = ({
  environment,
  historyObject = window.history,
  localAppOrigin = DEFAULT_POSTHOG_LOCAL_APP_ORIGIN,
  locationObject = window.location,
  now = Date.now(),
} = {}) => {
  let locationUrl;
  try {
    locationUrl = new URL(locationObject.href);
  } catch (error) {
    return null;
  }

  const queryParameters = new URLSearchParams(locationUrl.search);
  const {
    hashParameters,
    rawHash,
    stripped,
  } = stripPostHogFragment({
    historyObject,
    locationUrl,
  });
  if (
    !stripped
    || !hasPostHogProperties(hashParameters)
    || hasPostHogProperties(queryParameters)
    || !isUnambiguousHash(rawHash)
    || !hasCompleteHandoffTuple(hashParameters)
  ) {
    return null;
  }

  const distinctID = hashParameters.get('ph_distinct_id');
  const handoffEnvironment = hashParameters.get('ph_environment');
  const sessionID = hashParameters.get('ph_session_id');
  const approvedAppOrigin = environment === 'local'
    ? getPostHogLocalAppOrigin(localAppOrigin)
    : APP_ORIGINS_BY_ENVIRONMENT[environment];
  if (
    hashParameters.get('ph_handoff') !== HANDOFF_VERSION
    || handoffEnvironment !== environment
    || approvedAppOrigin !== locationUrl.origin
    || !isValidAnonymousDistinctId(distinctID)
    || !isValidAnonymousSessionId(sessionID, now)
  ) {
    return null;
  }

  return {
    distinctID,
    environment: handoffEnvironment,
    sessionID,
    version: HANDOFF_VERSION,
  };
};
